import { Injectable, inject, signal, OnDestroy, DestroyRef, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';
import { WebSocketService, WebSocketMessage } from '../../../core/services/websocket.service';
import { AuthService } from '../../../core/services/auth.service';
import { ConfigStore, WS_BASE_POLLING_INTERVAL_MS, WS_POLLING_JITTER_MS } from '../../../stores/config.store';
import { LoggerService } from '../../../core/services/logger.service';
import { PermissionStore, Role, Permission, UserRole } from '../../../stores/permission.store';
import { AuthStore } from '../../../stores/auth.store';
import {
  PermissionWebSocketMessageAdapter,
  PermissionStatusMessage,
  PermissionStatusMessageType
} from './permission-websocket-message.adapter';

// Re-export types for convenience
export type { PermissionStatusMessage, PermissionStatusMessageType } from './permission-websocket-message.adapter';

/**
 * Configuration constants for WebSocket message handling
 */
export const WS_REFRESH_DEBOUNCE_MS = 500;
export const WS_RATE_LIMIT_WINDOW_MS = 1000;
export const WS_RATE_LIMIT_MAX_MESSAGES = 50;

/**
 * PermissionWebSocketService
 *
 * Manages real-time permission updates via WebSocket connection to the 'admin' endpoint.
 *
 * Features:
 * - Automatic connection with configurable parameters
 * - Adaptive polling backoff (reduces CPU usage when disconnected)
 * - Reconnection refresh debounce (prevents API overload)
 * - Message adapter pattern for format flexibility
 * - Real-time cache invalidation
 * - Multi-user permission sync
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionWebSocketService implements OnDestroy {
  private readonly webSocketService = inject(WebSocketService);
  private readonly configStore = inject(ConfigStore);
  private readonly logger = inject(LoggerService);
  // NG0200 circular-DI fix: resolve PermissionStore LAZILY via the Injector, not
  // an eager `inject(PermissionStore)` field. PermissionStore (providedIn:'root')
  // injects this service inside its ngrx/signals withHooks.onInit (GAP-FE-114),
  // which runs *while the PermissionStore token is still being constructed* (the
  // token is flagged CIRCULAR in the injector). An eager class-field
  // inject(PermissionStore) here runs at construction/field-init time and
  // re-enters that in-progress resolution -> the injector returns the CIRCULAR
  // sentinel -> NG0200. A captured Injector is always available and not part of
  // the cycle; the actual PermissionStore lookup is deferred to the async refresh
  // callers (scheduleRefresh / refreshPermissionsAfterReconnection), which only
  // fire after construction has unwound and the store is fully cached. Mirrors
  // the lazy-Injector pattern TenantStore uses to dodge this exact chain
  // (tenant.store.ts). NOTE: forwardRef() alone does NOT defer a field
  // initializer, so it is not sufficient here.
  private readonly injector = inject(Injector);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageAdapter = inject(PermissionWebSocketMessageAdapter);
  private readonly authService = inject(AuthService);

  // The backend `/ws/admin` upgrade requires one of these roles
  // (is_admin_websocket_user). Keep in lockstep with the backend check.
  private static readonly ADMIN_ROLES = ['admin', 'super_admin'];

  // Tracks whether the privileged socket pipeline is currently established,
  // so the role gate does not stack duplicate connect subscriptions.
  private adminSocketActive = false;

  // Connection state tracking
  private readonly _connectionStatus = signal<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  private readonly _lastMessageTimestamp = signal<Date | null>(null);
  private readonly _messageCount = signal(0);
  private readonly _lastPermissionChange = signal<PermissionStatusMessage | null>(null);

  // Track previous connection status for reconnection detection
  private previousConnectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error' = 'disconnected';

  // Refresh debounce tracking
  private lastRefreshTime = 0;
  private pendingRefresh: ReturnType<typeof setTimeout> | null = null;

  // Adaptive polling state
  private pollingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  // Fixed-interval fallback handle (adaptivePolling=false). Tracked in a field
  // so a reconnect (which re-invokes startAdaptivePolling) clears the prior
  // interval instead of stacking a new setInterval + onDestroy each time.
  private pollingIntervalId: ReturnType<typeof setInterval> | null = null;
  private currentPollingInterval = WS_BASE_POLLING_INTERVAL_MS;

  // Lifecycle tracking to prevent memory leaks
  private isDestroyed = false;

  // Rate limiting state
  private rateLimitWindowStart = 0;
  private messagesInWindow = 0;

  // Public readonly signals
  readonly connectionStatus = this._connectionStatus.asReadonly();
  readonly lastMessageTimestamp = this._lastMessageTimestamp.asReadonly();
  readonly messageCount = this._messageCount.asReadonly();
  readonly lastPermissionChange = this._lastPermissionChange.asReadonly();

  // WebSocket endpoint
  private readonly ADMIN_ENDPOINT = 'admin';

  constructor() {
    // Passive connection monitoring is always safe (it backs off while
    // disconnected) — start it once.
    this.monitorConnectionState();

    // ROLE GATE: `/ws/admin` rejects the upgrade for non-admins (401/403).
    // This service is reachable via the root PermissionStore, so without the
    // gate every signed-in user opened the privileged socket and reconnect-
    // looped in the console. Only connect while the session holds an
    // admin/super_admin role; react to login/logout.
    this.authService.currentUser$
      .pipe(
        map(() => this.authService.hasAnyRole(PermissionWebSocketService.ADMIN_ROLES)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(isAdmin => {
        if (isAdmin) {
          this.connectAdminSocket();
        } else {
          this.disconnect();
        }
      });
  }

  /**
   * Open the privileged admin socket, guarded by the admin role.
   *
   * Idempotent: a no-op if the pipeline is already established or the current
   * session lacks the required role (defence-in-depth against callers like
   * `reconnect()` that can fire outside the auth gate).
   */
  private connectAdminSocket(): void {
    if (this.adminSocketActive) {
      return;
    }
    if (!this.authService.hasAnyRole(PermissionWebSocketService.ADMIN_ROLES)) {
      this.logger.debug('PermissionWebSocketService: admin role absent — not opening /ws/admin');
      return;
    }
    this.adminSocketActive = true;
    this.initializeWebSocketConnection();
  }

  /**
   * Initialize WebSocket connection to admin endpoint
   */
  private initializeWebSocketConnection(): void {
    const wsEndpoints = this.configStore.ws();
    const wsConfig = this.configStore.wsConnectionConfig();
    const adminUrl = wsEndpoints.admin;

    this.logger.info('PermissionWebSocketService: Initializing connection to admin endpoint', {
      endpoint: this.ADMIN_ENDPOINT,
      url: adminUrl,
      config: wsConfig
    });

    this._connectionStatus.set('connecting');

    // Connect using configurable parameters
    this.webSocketService.connect(this.ADMIN_ENDPOINT, {
      url: adminUrl,
      reconnect: true,
      reconnectInterval: wsConfig.reconnectInterval,
      maxReconnectAttempts: wsConfig.maxReconnectAttempts,
      heartbeatInterval: wsConfig.heartbeatInterval
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((message: WebSocketMessage) => this.messageAdapter.isPermissionStatusMessage(message))
      )
      .subscribe({
        next: (message) => this.handleWebSocketMessage(message),
        error: (error) => this.handleConnectionError(error),
        complete: () => this.handleConnectionClosed()
      });

    // (Re)start adaptive polling for THIS connection. `disconnect()` stops it,
    // so it must be (re)started on every connect — including a reconnect after
    // the role gate re-opens the socket on a later admin login.
    this.startAdaptivePolling();
  }

  /**
   * Subscribe once to the connection-status stream.
   *
   * The connectionStatus$ subscription and the timer destroy cleanup are
   * lifecycle-scoped (set up once in the constructor), NOT per-connect — a
   * per-connect subscription stacked a fresh listener on every reconnect.
   */
  private monitorConnectionState(): void {
    // Subscribe to connection status observable for reactive updates
    this.webSocketService.connectionStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(status => {
        this.handleConnectionStatusChange(status);
      });

    // Cleanup on destroy
    this.destroyRef.onDestroy(() => {
      if (this.pollingTimeoutId) {
        clearTimeout(this.pollingTimeoutId);
        this.pollingTimeoutId = null;
      }
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
      }
      if (this.pendingRefresh) {
        clearTimeout(this.pendingRefresh);
        this.pendingRefresh = null;
      }
    });
  }

  /**
   * Start adaptive polling for connection state
   * Increases interval when disconnected, resets when connected
   */
  private startAdaptivePolling(): void {
    // Idempotent: a reconnect re-invokes this; never stack poll loops/intervals.
    if (this.pollingTimeoutId) {
      clearTimeout(this.pollingTimeoutId);
      this.pollingTimeoutId = null;
    }
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }

    const wsConfig = this.configStore.wsConnectionConfig();

    if (!wsConfig.adaptivePolling) {
      // Fallback to fixed interval if adaptive polling disabled. Stored in a
      // field (cleared by the guard above + disconnect/destroy) so reconnects
      // do not stack intervals.
      this.pollingIntervalId = setInterval(() => {
        const state = this.webSocketService.getConnectionState(this.ADMIN_ENDPOINT);
        if (state) {
          this.handleConnectionStatusChange(state);
        }
      }, 1000);
      return;
    }

    const poll = () => {
      // Prevent memory leak: stop polling if service is destroyed
      if (this.isDestroyed) {
        this.pollingTimeoutId = null;
        return;
      }

      const state = this.webSocketService.getConnectionState(this.ADMIN_ENDPOINT);
      if (state) {
        this.handleConnectionStatusChange(state);
      }

      // Adaptive backoff: increase interval when disconnected
      if (this._connectionStatus() !== 'connected') {
        this.currentPollingInterval = Math.min(
          this.currentPollingInterval * 1.5,
          wsConfig.maxPollingInterval
        );
      } else {
        // Reset with jitter to prevent thundering herd
        const jitter = Math.floor(Math.random() * WS_POLLING_JITTER_MS);
        this.currentPollingInterval = WS_BASE_POLLING_INTERVAL_MS + jitter;
      }

      // Only schedule next poll if not destroyed
      if (!this.isDestroyed) {
        this.pollingTimeoutId = setTimeout(poll, this.currentPollingInterval);
      }
    };

    // Start polling
    this.pollingTimeoutId = setTimeout(poll, this.currentPollingInterval);
  }

  /**
   * Handle connection status change with reconnection detection
   */
  private handleConnectionStatusChange(newStatus: 'connecting' | 'connected' | 'disconnected' | 'error'): void {
    const wasDisconnected = this.previousConnectionStatus === 'disconnected' ||
                            this.previousConnectionStatus === 'error';
    const isNowConnected = newStatus === 'connected';

    this._connectionStatus.set(newStatus);

    // Detect reconnection and trigger debounced refresh
    if (wasDisconnected && isNowConnected) {
      this.logger.info('PermissionWebSocketService: Reconnection detected, triggering state replay');
      this.onReconnected();
    }

    this.previousConnectionStatus = newStatus;
  }

  /**
   * Handle reconnection with debounced refresh
   * Prevents API overload from rapid reconnection cycles
   */
  private onReconnected(): void {
    const wsConfig = this.configStore.wsConnectionConfig();
    const now = Date.now();
    const timeSinceLastRefresh = now - this.lastRefreshTime;

    this.logger.info('PermissionWebSocketService: Triggering state refresh after reconnection');

    // Re-subscribe to permission events
    this.subscribeToPermissionEvents([
      'role_created',
      'role_updated',
      'role_deleted',
      'permission_granted',
      'permission_revoked',
      'user_role_assigned',
      'user_role_removed',
      'permissions_updated',
      'cache_invalidated'
    ]);

    // Debounce refresh to prevent API overload
    if (timeSinceLastRefresh > wsConfig.minRefreshInterval) {
      this.lastRefreshTime = now;
      this.refreshPermissionsAfterReconnection();
    } else {
      this.logger.debug('PermissionWebSocketService: Skipping refresh - too soon since last refresh', {
        timeSinceLastRefresh,
        minInterval: wsConfig.minRefreshInterval
      });
    }
  }

  /**
   * Refresh permissions data after reconnection
   */
  private refreshPermissionsAfterReconnection(): void {
    this.logger.info('PermissionWebSocketService: Refreshing permissions after reconnection');
    // Lazy resolve (see Injector field comment): runs after construction, store cached.
    this.injector.get(PermissionStore).refresh();
  }

  /**
   * Check if message should be rate limited
   * Returns true if the message should be dropped
   */
  private isRateLimited(): boolean {
    const now = Date.now();

    // Reset window if expired
    if (now - this.rateLimitWindowStart > WS_RATE_LIMIT_WINDOW_MS) {
      this.rateLimitWindowStart = now;
      this.messagesInWindow = 0;
    }

    this.messagesInWindow++;

    if (this.messagesInWindow > WS_RATE_LIMIT_MAX_MESSAGES) {
      this.logger.warn('PermissionWebSocketService: Rate limit exceeded, dropping message', {
        messagesInWindow: this.messagesInWindow,
        limit: WS_RATE_LIMIT_MAX_MESSAGES,
        windowMs: WS_RATE_LIMIT_WINDOW_MS
      });
      return true;
    }

    return false;
  }

  /**
   * Handle incoming WebSocket message using adapter
   * Includes rate limiting protection against message flooding
   */
  private handleWebSocketMessage(message: WebSocketMessage): void {
    // Rate limit check to prevent DoS via message flooding
    if (this.isRateLimited()) {
      return;
    }

    try {
      const permissionMessage = this.messageAdapter.toPermissionStatusMessage(message);

      if (!permissionMessage) {
        this.logger.warn('PermissionWebSocketService: Could not parse permission status message', { message });
        return;
      }

      this.logger.debug('PermissionWebSocketService: Received permission status message', {
        type: permissionMessage.type,
        entityId: permissionMessage.entityId,
        entityType: permissionMessage.entityType,
        initiatedBy: permissionMessage.initiatedBy
      });

      this._lastMessageTimestamp.set(new Date(permissionMessage.timestamp));
      this._messageCount.update(count => count + 1);
      this._lastPermissionChange.set(permissionMessage);

      this.processPermissionMessage(permissionMessage);

    } catch (error) {
      this.logger.error('PermissionWebSocketService: Error handling WebSocket message', { error, message });
    }
  }

  /**
   * Process permission message and update store accordingly
   */
  private processPermissionMessage(message: PermissionStatusMessage): void {
    const currentUser = this.authStore.currentUser();

    switch (message.type) {
      case 'role_created':
        if (message.data) {
          this.handleRoleCreated(message.data as Role);
        }
        break;

      case 'role_updated':
        if (message.data) {
          this.handleRoleUpdated(message.data as Role);
        }
        break;

      case 'role_deleted':
        this.handleRoleDeleted(message.entityId);
        break;

      case 'permission_granted':
      case 'permission_revoked':
        // Refresh permissions for the affected user
        if (message.userId === currentUser?.id) {
          this.scheduleRefresh();
        }
        break;

      case 'user_role_assigned':
      case 'user_role_removed':
        // Refresh if affects current user
        if (message.userId === currentUser?.id) {
          this.scheduleRefresh();
        }
        break;

      case 'permissions_updated':
      case 'cache_invalidated':
        // Full refresh needed
        this.scheduleRefresh();
        break;

      default:
        this.logger.warn('PermissionWebSocketService: Unknown message type', { type: message.type });
    }
  }

  /**
   * Handle role created event - add to store
   */
  private handleRoleCreated(role: Role): void {
    this.logger.info('PermissionWebSocketService: Role created via WebSocket', { roleId: role.id, roleName: role.name });
    // Schedule refresh to get the new role with all relationships
    this.scheduleRefresh();
  }

  /**
   * Handle role updated event - update in store
   */
  private handleRoleUpdated(role: Role): void {
    this.logger.info('PermissionWebSocketService: Role updated via WebSocket', { roleId: role.id, roleName: role.name });
    // Schedule refresh to get updated role data
    this.scheduleRefresh();
  }

  /**
   * Handle role deleted event - remove from store
   */
  private handleRoleDeleted(roleId: string): void {
    this.logger.info('PermissionWebSocketService: Role deleted via WebSocket', { roleId });
    // Schedule refresh to sync state
    this.scheduleRefresh();
  }

  /**
   * Schedule a debounced refresh to batch multiple updates
   */
  private scheduleRefresh(): void {
    if (this.pendingRefresh) {
      clearTimeout(this.pendingRefresh);
    }

    this.pendingRefresh = setTimeout(() => {
      this.logger.info('PermissionWebSocketService: Executing scheduled refresh');
      // Lazy resolve (see Injector field comment): runs after construction, store cached.
      this.injector.get(PermissionStore).refresh();
      this.pendingRefresh = null;
    }, WS_REFRESH_DEBOUNCE_MS);
  }

  private handleConnectionError(error: unknown): void {
    this.logger.error('PermissionWebSocketService: Connection error', { error });
    this._connectionStatus.set('error');
  }

  private handleConnectionClosed(): void {
    this.logger.info('PermissionWebSocketService: Connection closed');
    this._connectionStatus.set('disconnected');
  }

  isConnected(): boolean {
    return this.webSocketService.isConnected(this.ADMIN_ENDPOINT);
  }

  disconnect(): void {
    this.adminSocketActive = false;
    this.logger.info('PermissionWebSocketService: Disconnecting from admin endpoint');
    this.webSocketService.disconnect(this.ADMIN_ENDPOINT);
    this._connectionStatus.set('disconnected');

    if (this.pollingTimeoutId) {
      clearTimeout(this.pollingTimeoutId);
      this.pollingTimeoutId = null;
    }

    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }

    if (this.pendingRefresh) {
      clearTimeout(this.pendingRefresh);
      this.pendingRefresh = null;
    }
  }

  reconnect(): void {
    this.logger.info('PermissionWebSocketService: Reconnecting to admin endpoint');
    this.disconnect();
    // Re-establishes only if the session still holds the admin role.
    setTimeout(() => this.connectAdminSocket(), 100);
  }

  /**
   * Allowed message types for security whitelist validation (DEV-1448)
   * Only these message types are permitted to be sent through the WebSocket
   * to prevent injection of malicious or unexpected message types.
   */
  private readonly ALLOWED_MESSAGE_TYPES = [
    'subscribe',
    'unsubscribe',
    'request_sync',
    'permission_change',
    'ping',
    'pong'
  ] as const;

  send(message: Record<string, unknown>): void {
    // Security (DEV-1448): Whitelist validation for message types
    // Prevents injection of malicious or unexpected message types
    if (message['type'] !== undefined) {
      const messageType = message['type'];
      if (typeof messageType !== 'string' || !this.ALLOWED_MESSAGE_TYPES.includes(messageType as typeof this.ALLOWED_MESSAGE_TYPES[number])) {
        this.logger.warn('PermissionWebSocketService: Invalid message type rejected', {
          type: messageType,
          allowedTypes: this.ALLOWED_MESSAGE_TYPES
        });
        return;
      }
    }

    if (this.isConnected()) {
      this.webSocketService.send(this.ADMIN_ENDPOINT, message);
    } else {
      this.logger.warn('PermissionWebSocketService: Cannot send message - not connected');
    }
  }

  subscribeToPermissionEvents(eventTypes: PermissionStatusMessageType[]): void {
    this.send({
      type: 'subscribe',
      payload: {
        events: eventTypes,
        topic: 'permission_management'
      }
    });
  }

  unsubscribeFromPermissionEvents(eventTypes: PermissionStatusMessageType[]): void {
    this.send({
      type: 'unsubscribe',
      payload: {
        events: eventTypes,
        topic: 'permission_management'
      }
    });
  }

  /**
   * Request permission sync for a specific user
   */
  requestUserPermissionSync(userId: string): void {
    this.send({
      type: 'request_sync',
      payload: {
        topic: 'permission_management',
        userId
      }
    });
  }

  /**
   * Notify other clients of a permission change
   */
  notifyPermissionChange(change: {
    type: PermissionStatusMessageType;
    entityId: string;
    entityType: 'role' | 'permission' | 'user_role';
    userId?: string;
    roleId?: string;
  }): void {
    this.send({
      type: 'permission_change',
      payload: {
        ...change,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Get current polling interval (for testing/debugging)
   */
  getCurrentPollingInterval(): number {
    return this.currentPollingInterval;
  }

  /**
   * Get last refresh time (for testing/debugging)
   */
  getLastRefreshTime(): number {
    return this.lastRefreshTime;
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.disconnect();
  }

  /**
   * Get rate limit status (for testing/debugging)
   */
  getRateLimitStatus(): { messagesInWindow: number; windowStart: number } {
    return {
      messagesInWindow: this.messagesInWindow,
      windowStart: this.rateLimitWindowStart
    };
  }

  /**
   * Reset rate limit (for testing)
   */
  resetRateLimit(): void {
    this.rateLimitWindowStart = 0;
    this.messagesInWindow = 0;
  }
}
